import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { Label } from "../ui/label";
import { Button } from "../ui/button";
import { Users, TrendingUp, FileText, Hash, User, IdCard, MapPin, CircleDollarSign, Megaphone } from "lucide-react";
import { PDFDownloadLink } from "@react-pdf/renderer";
import ReservationPDF from "../ReservationPDF";
import { useAuth } from "../../context/AuthContext";


const formatMoney = (n) => new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN", maximumFractionDigits: 0 }).format(Number(n) || 0);
const fmtDate = (iso) => { try { return new Date(iso).toLocaleString("es-MX", { dateStyle: "short", timeStyle: "short" }); } catch { return iso; } };
const RES_BADGE = { pending: "bg-amber-100 text-amber-800 border border-amber-200", approved: "bg-emerald-100 text-emerald-800 border border-emerald-200", rejected: "bg-red-100 text-red-800 border border-red-200" };

export default function ReservationDetailDialog({ reservation, onClose }) {
  // Verificación de rol para seguridad en la vista
  const isAdmin = localStorage.getItem("role") === "admin";
  const { user, ready, logout } = useAuth();
  const userRole = user?.role || "vendedor";

  if (!reservation) return null;

  return (
    <Dialog open={!!reservation} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="reservation-detail-dialog" className="max-w-4xl p-0 overflow-hidden rounded-2xl bg-slate-50">
        
        {/* ENCABEZADO */}
        <DialogHeader className="p-6 pb-4 border-b border-slate-200 bg-white flex flex-row justify-between items-center">
          <div>
            <DialogTitle className="font-display text-2xl font-bold flex items-center gap-2 text-slate-900">
              Detalle de Reserva <span className="text-blue-600">#{reservation.reservation_id}</span>
            </DialogTitle>
            <p className="text-slate-500 text-sm mt-1">Registrada el {fmtDate(reservation.created_at)}</p>
          </div>
          <div className="text-right">
            <span className={`inline-flex px-3 py-1 rounded-full text-xs font-bold tracking-wide uppercase shadow-sm ${RES_BADGE[reservation.status]}`}>
              {reservation.status === "pending" ? "Pendiente" : reservation.status === "approved" ? "Aprobada" : "Rechazada"}
            </span>
          </div>
        </DialogHeader>

        {/* CONTENIDO (2 COLUMNAS) */}
        <div className="p-6 grid grid-cols-1 lg:grid-cols-2 gap-6 overflow-y-auto max-h-[70vh]">
          
          {/* ================= COLUMNA IZQUIERDA ================= */}
          <div className="space-y-6">
            
            {/* Lote / ID */}
            <div className="p-5 bg-white border border-slate-200 rounded-xl shadow-sm hidden ">
              <h4 className="font-bold text-sm flex items-center gap-2 text-slate-800 border-b border-slate-100 pb-2 mb-4">
                <Hash size={16} className="text-slate-400" /> Identificador de Lote
              </h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">ID Reserva</Label>
                  <p className="font-mono text-lg font-semibold text-slate-900">{reservation.reservation_id}</p>
                </div>
                <div>
                  <Label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Lote Asignado</Label>
                  <p className="font-mono text-lg font-semibold text-blue-700">{reservation.id_lots}</p>
                </div>
              </div>
            </div>

            {/* Info Principal */}
            <div className="p-5 bg-white border border-slate-200 rounded-xl shadow-sm">
              <h4 className="font-bold text-sm flex items-center gap-2 text-slate-800 border-b border-slate-100 pb-2 mb-4">
                <User size={16} className="text-blue-500" /> Información del Cliente
              </h4>
              <div className="space-y-4 text-sm">
                <div>
                  <Label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Nombre Completo</Label>
                  <p className="font-medium text-slate-900 text-base">{reservation.cliente?.nombre} {reservation.cliente?.apellido_paterno} {reservation.cliente?.apellido_materno}</p>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div><Label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Edad</Label><p className="text-slate-700">{reservation.cliente?.age || "-"}</p></div>
                  <div><Label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Fecha de Nacimiento</Label><p className="text-slate-700">{reservation.cliente?.fecha_nacimiento || "-"}</p></div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div><Label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Teléfono</Label><p className="text-slate-700">{reservation.cliente?.telefono || "-"}</p></div>
                  <div><Label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Correo Electrónico</Label><p className="text-slate-700 truncate">{reservation.cliente?.email || "-"}</p></div>
                </div>
              </div>
            </div>

            {/* Datos Personales */}
            <div className="p-5 bg-white border border-slate-200 rounded-xl shadow-sm">
              <h4 className="font-bold text-sm flex items-center gap-2 text-slate-800 border-b border-slate-100 pb-2 mb-4">
                <IdCard size={16} className="text-purple-500" /> Perfil Demográfico y Laboral
              </h4>
              <div className="grid grid-cols-2 gap-x-4 gap-y-4 text-sm">
                <div><Label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Estado Civil</Label><p className="text-slate-700">{reservation.datos_cliente?.estatus_conyugal || "-"}</p></div>
                <div><Label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Nacionalidad</Label><p className="text-slate-700">{reservation.datos_cliente?.nacionalidad || "-"}</p></div>
                <div className="col-span-2"><Label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Beneficiario</Label><p className="text-slate-700">{reservation.datos_cliente?.beneficiario || "-"}</p></div>
                <div><Label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Tipo de Casa</Label><p className="text-slate-700">{reservation.datos_cliente?.tipo_casa || "-"}</p></div>
                <div><Label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Dependientes</Label><p className="text-slate-700">{reservation.datos_cliente?.dependientes || 0}</p></div>
                <div><Label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Ocupación</Label><p className="text-slate-700">{reservation.datos_cliente?.occupacion || "-"}</p></div>
                <div className="col-span-2"><Label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Empresa / Antigüedad</Label><p className="text-slate-700">{reservation.datos_cliente?.lugar_trabajo || "-"} ({reservation.datos_cliente?.antiguedad_trabajo || "-"})</p></div>
              </div>
            </div>

            {/* Dirección */}
            <div className="p-5 bg-white border border-slate-200 rounded-xl shadow-sm">
              <h4 className="font-bold text-sm flex items-center gap-2 text-slate-800 border-b border-slate-100 pb-2 mb-4">
                <MapPin size={16} className="text-emerald-500" /> Dirección de Residencia
              </h4>
              <div className="space-y-4 text-sm">
                <div className="grid grid-cols-3 gap-4">
                  <div className="col-span-2"><Label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Calle</Label><p className="text-slate-700">{reservation.direccion?.calle || "-"}</p></div>
                  <div><Label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Número</Label><p className="text-slate-700">{reservation.direccion?.numero || "-"}</p></div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div><Label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Colonia</Label><p className="text-slate-700">{reservation.direccion?.colonia || "-"}</p></div>
                  <div><Label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Código Postal</Label><p className="text-slate-700">{reservation.direccion?.codigo_postal || "-"}</p></div>
                  <div><Label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Municipio</Label><p className="text-slate-700">{reservation.direccion?.municipio || "-"}</p></div>
                  <div><Label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Estado</Label><p className="text-slate-700">{reservation.direccion?.estado || "-"}</p></div>
                </div>
              </div>
            </div>

          </div>

          {/* ================= COLUMNA DERECHA ================= */}
          <div className="space-y-6">
            
            {/* Aportaciones (SOLO ADMIN) */}
            {userRole === 'admin' && (
              <div className="p-5 bg-white border border-slate-200 rounded-xl shadow-sm">
                <h4 className="font-bold text-sm flex items-center gap-2 text-slate-800 border-b border-slate-100 pb-2 mb-4">
                  <CircleDollarSign size={16} className="text-amber-500" /> Esquema de Pagos
                </h4>
                <div className="space-y-4 text-sm">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Valor Total Lote</Label>
                      <p className="font-mono text-lg font-bold text-slate-900">{formatMoney(reservation.contributions?.total_contribution)}</p>
                    </div>
                    <div>
                      <Label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Pago de Apartado</Label>
                      <p className="font-mono text-base font-semibold text-slate-700">{formatMoney(reservation.contributions?.reservation_payment)}</p>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div><Label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Enganche Inicial</Label><p className="font-mono text-slate-700">{formatMoney(reservation.contributions?.initial_contribution)}</p></div>
                    <div><Label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Día Primer Pago</Label><p className="text-slate-700">Día {reservation.contributions?.first_monthly_payment_day || "-"}</p></div>
                  </div>

                  <div className="p-3 bg-slate-50 border border-slate-100 rounded-lg flex justify-between items-center">
                    <div>
                      <Label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Plazo (Meses)</Label>
                      <p className="font-medium">{reservation.contributions?.monthly_contribution} meses</p>
                    </div>
                    <div className="text-right">
                      <Label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Monto Mensual</Label>
                      <p className="font-mono text-base font-bold text-blue-700">{formatMoney(reservation.contributions?.monthly_amount)}</p>
                    </div>
                  </div>

                  <div>
                    <Label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Valor Total (Texto)</Label>
                    <p className="text-slate-700 italic">{reservation.contributions?.total_contribution_text || "-"}</p>
                  </div>
                  
                  {reservation.contributions?.notes && (
                    <div>
                      <Label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Notas sobre Pagos</Label>
                      <div className="mt-1 p-3 bg-amber-50/50 border border-amber-100 rounded-lg text-xs text-amber-900 whitespace-pre-wrap">
                        {reservation.contributions.notes}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Promotor */}
            <div className="p-5 bg-white border border-slate-200 rounded-xl shadow-sm">
              <h4 className="font-bold text-sm flex items-center gap-2 text-slate-800 border-b border-slate-100 pb-2 mb-4">
                <Megaphone size={16} className="text-indigo-500" /> Datos del Promotor
              </h4>
              <div className="grid grid-cols-2 gap-x-4 gap-y-4 text-sm">
                <div className="col-span-2"><Label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Nombre</Label><p className="font-medium text-slate-900">{reservation.promoter?.name || "-"}</p></div>
                <div><Label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Teléfono</Label><p className="text-slate-700">{reservation.promoter?.office_phone || "-"}</p></div>
                <div><Label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Email</Label><p className="text-slate-700">{reservation.promoter?.office_email || "-"}</p></div>
                <div className="col-span-2"><Label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Oficina</Label><p className="text-slate-700">{reservation.promoter?.office_address || "-"}</p></div>
              </div>
            </div>

            {/* Notas Generales */}
            <div className="p-5 bg-slate-100/50 border border-slate-200 rounded-xl shadow-sm">
              <h4 className="font-bold text-sm flex items-center gap-2 text-slate-600 border-b border-slate-200/60 pb-2 mb-4">
                <FileText size={16} className="text-slate-400" /> Observaciones de la Reserva
              </h4>
              <p className="text-sm text-slate-600 whitespace-pre-wrap italic leading-relaxed">
                {reservation.notes || "No hay notas adicionales registradas en esta reserva."}
              </p>
            </div>

          </div>
        </div>

        {/* PIE / BOTONES */}
        <DialogFooter className="p-6 bg-white border-t border-slate-200 gap-3">
          <Button variant="outline" className="h-11 px-6 border-slate-300" asChild>
            <PDFDownloadLink
              document={<ReservationPDF reservation={reservation} />}
              fileName={`solicitud_${reservation.id_lots}.pdf`}
              style={{ textDecoration: 'none', color: 'inherit', display: 'flex', alignItems: 'center', gap: '8px' }}
            >
              {({ loading }) => (
                loading ? "Generando PDF..." : <><FileText size={16} className="text-red-500" /> Descargar Solicitud PDF</>
              )}
            </PDFDownloadLink>
          </Button>
          <Button onClick={onClose} className="h-11 px-8 bg-slate-900 hover:bg-slate-800 text-white shadow-lg shadow-slate-900/10">
            Cerrar Vista
          </Button>
        </DialogFooter>

      </DialogContent>
    </Dialog>
  );
}